import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { TutorialStep } from './tutorialsModel';

interface ControlsProps {
  steps: TutorialStep[];
  index: number;
  playing: boolean;
  progress: number;
  captions: boolean;
  onCaptions: (on: boolean) => void;
  onToggle: () => void;
  onGoTo: (index: number) => void;
}

/**
 * Step dots: one segment per step (seen steps full, the current one filling with the narration). Each segment is a
 * button to jump there; flex-1, so any number of steps fits one row at 375 px.
 */
function StepDots({ steps, index, progress, onGoTo }: Pick<ControlsProps, 'steps' | 'index' | 'progress' | 'onGoTo'>) {
  return (
    <ol className="flex w-full items-center gap-1" aria-label="Steps">
      {steps.map((s, i) => {
        const fill = i < index ? 1 : i === index ? progress : 0;
        return (
          <li key={s.key} className="min-w-0 flex-1">
            <button
              type="button"
              data-testid={`lesson-dot-${i}`}
              aria-label={`Step ${i + 1}: ${s.title}`}
              aria-current={i === index ? 'step' : undefined}
              onClick={() => onGoTo(i)}
              className="group flex h-6 w-full items-center rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 max-sm:h-11"
            >
              <span className="relative h-1.5 w-full overflow-hidden rounded-full bg-surface-3 transition-[height] duration-150 ease-out group-hover:h-2 motion-reduce:transition-none">
                <span className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${Math.round(fill * 1000) / 10}%` }} />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function IconButton({ label, icon, onClick, disabled, testId, fill }: { label: string; icon: string; onClick: () => void; disabled?: boolean; testId: string; fill?: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} data-testid={testId} onClick={onClick} disabled={disabled}>
          <Icon name={icon} size={22} fill={fill} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Previous / play-pause / next, the step counter and the captions switch, under the step dots. */
export function PlayerControls({ steps, index, playing, progress, captions, onCaptions, onToggle, onGoTo }: ControlsProps) {
  const last = steps.length - 1;
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card px-2 py-1.5 shadow-card sm:px-3">
      <StepDots steps={steps} index={index} progress={progress} onGoTo={onGoTo} />
      <div className="flex items-center gap-1">
        <IconButton label="Previous step (←)" icon="skip_previous" testId="lesson-prev" onClick={() => onGoTo(index - 1)} disabled={index === 0} fill />
        <IconButton label={playing ? 'Pause (space)' : 'Play (space)'} icon={playing ? 'pause' : 'play_arrow'} testId="lesson-play" onClick={onToggle} fill />
        <IconButton label="Next step (→)" icon="skip_next" testId="lesson-next" onClick={() => onGoTo(index + 1)} disabled={index === last} fill />
        <span data-testid="lesson-step-count" className="tabular ml-1 text-xs text-muted-foreground">
          Step {index + 1} of {steps.length}
        </span>
        <Toggle pressed={captions} onPressedChange={onCaptions} aria-label="Captions" data-testid="lesson-captions" className="ml-auto max-sm:size-11">
          <Icon name="closed_caption" size={20} fill={captions} />
        </Toggle>
      </div>
    </div>
  );
}
